import { useEffect, useRef, useState } from "react";
import { usePage } from "@inertiajs/react";
import toast, { Toaster } from "react-hot-toast";
import SmartLink from "@/components/marketing/smart-link";
import {
  companyProfile,
  mainNavigation,
  siteLinks
} from "@/data/marketing-site";

function isActive(currentPage, activeKeys = []) {
  return activeKeys.includes(currentPage);
}

export default function PublicLayout({ children }) {
  const { props } = usePage();
  const marketing = props?.marketing || {};
  const currentPage = marketing.current_page || "home";
  const loginPath = props?.app_url;
  const appPath = props?.app_url;
  const authenticated = props?.authenticated === true;
  const flash = props?.flash || {};
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const primaryAction = authenticated
    ? { label: "Åbn appen", href: appPath }
    : { label: "Log ind", href: loginPath };

  useEffect(() => {
    document.body.classList.toggle("public-nav-open", mobileMenuOpen);

    return () => {
      document.body.classList.remove("public-nav-open");
    };
  }, [mobileMenuOpen]);

  useEffect(() => {
    setMobileMenuOpen(false);
  }, [currentPage]);

  useEffect(() => {
    if (!flash.notice) return;

    toast.success(flash.notice);
  }, [flash?.key, flash?.notice]);

  useEffect(() => {
    if (!flash.alert) return;

    toast.error(flash.alert);
  }, [flash?.key, flash?.alert]);

  return (
    <div className="public-site">
      <Toaster position="bottom-center" />
      <header className="public-header">
        <div className="public-container public-header__inner">
          <SmartLink href={siteLinks.home} className="public-brand">
            <span className="public-brand__copy">
              <strong>obsero</strong>
            </span>
          </SmartLink>

          <nav className="public-nav d-none d-lg-flex" aria-label="Primær navigation">
            {mainNavigation.map((item) => (
              <SmartLink
                key={item.key}
                href={item.href}
                className={`public-nav__link ${isActive(currentPage, item.activeKeys) ? "is-active" : ""}`}
              >
                {item.label}
              </SmartLink>
            ))}
          </nav>

          <div className="public-header__actions d-none d-lg-flex">
            <SmartLink href={siteLinks.demo} className="btn btn-outline-secondary">
              Book demo
            </SmartLink>
            <SmartLink href={primaryAction.href} className="btn btn-primary">
              {primaryAction.label}
            </SmartLink>
          </div>

          <button
            type="button"
            className="public-header__toggle d-lg-none"
            aria-expanded={mobileMenuOpen}
            aria-label={mobileMenuOpen ? "Luk navigation" : "Åbn navigation"}
            onClick={() => setMobileMenuOpen((open) => !open)}
          >
            <i className={`bi ${mobileMenuOpen ? "bi-x-lg" : "bi-list"}`} aria-hidden="true" />
          </button>
        </div>
      </header>

      <div className={`public-mobile-nav ${mobileMenuOpen ? "is-open" : ""}`}>
        <div className="public-container public-mobile-nav__inner">
          <div className="public-mobile-nav__section">
            {mainNavigation.map((item) => (
              <SmartLink
                key={item.key}
                href={item.href}
                className={`public-mobile-nav__link ${isActive(currentPage, item.activeKeys) ? "is-active" : ""}`}
              >
                {item.label}
              </SmartLink>
            ))}
          </div>
          <div className="public-mobile-nav__actions">
            <SmartLink href={siteLinks.demo} className="btn btn-outline-secondary">
              Book demo
            </SmartLink>
            <SmartLink href={primaryAction.href} className="btn btn-primary">
              {primaryAction.label}
            </SmartLink>
          </div>
        </div>
      </div>

      <main className="public-main">{children}</main>

      <footer className="public-footer">
        <div className="public-container">
          <div className="public-footer__grid">
            <div className="public-footer__about">
              <div className="public-brand public-brand--footer">
                <span className="public-brand__copy">
                  <strong>obsero</strong>
                </span>
              </div>
              <p className="public-footer__copy">
                Vi omsætter komplekse lovkrav til operationel drift, så governance, evidens og ledelsesansvar hænger sammen i praksis.
              </p>
            </div>

            <div className="public-footer__solutions">
              <div className="public-footer__label">Løsninger</div>
              <div className="public-footer__links">
                <SmartLink href={siteLinks.core} className="public-footer__link">Obsero Core</SmartLink>
                <SmartLink href={siteLinks.enterprise} className="public-footer__link">Enterprise løsning</SmartLink>
              </div>
            </div>

            <div className="public-footer__contact">
              <div className="public-footer__label">Kontakt</div>
              <div className="public-footer__links">
                <SmartLink href={companyProfile.emailHref} className="public-footer__link">
                  {companyProfile.email}
                </SmartLink>
                <SmartLink href={companyProfile.phoneHref} className="public-footer__link">
                  {companyProfile.phone}
                </SmartLink>
                <span className="public-footer__meta-line">{companyProfile.address}</span>
                <span className="public-footer__meta-line">{companyProfile.postalCity}</span>
                <span className="public-footer__meta-line">CVR {companyProfile.cvr}</span>
              </div>
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
}
